var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as R from 'ramda';
import { utils, isEmitComponentEventActionId, emitComponentEvent, setMaxId, resetMaxId, } from '@tencent/gems-shared';
/** 素材类型 */
var MaterialType;
(function (MaterialType) {
    /** 动作 */
    MaterialType["ACTION"] = "actions";
    /** 组件 */
    MaterialType["COMPONENT"] = "components";
    /** 插件 */
    MaterialType["PLUGIN"] = "plugins";
})(MaterialType || (MaterialType = {}));
/** 获取素材 */
var getMaterial = function (type, materialId, materials) {
    var material = materials[type];
    if (Array.isArray(material)) {
        return material.find(function (i) { return i.id === materialId; });
    }
    if (material instanceof Map) {
        return material.get(materialId);
    }
    return material === null || material === void 0 ? void 0 : material[materialId];
};
/** 获取动作素材 */
var getAction = function (schemaOrInstance, materials) {
    var id = schemaOrInstance.id, name = schemaOrInstance.name;
    var action = getMaterial(MaterialType.ACTION, id, materials) || getMaterial(MaterialType.ACTION, name, materials);
    if (action) {
        return action;
    }
    if (isEmitComponentEventActionId(Number(id))) {
        return {
            action: emitComponentEvent,
            id: Number.MAX_SAFE_INTEGER,
        };
    }
};
/** 获取组件素材 */
var getComponent = function (schemaOrInstance, materials) {
    var id = schemaOrInstance.id, name = schemaOrInstance.name;
    return getMaterial(MaterialType.COMPONENT, id, materials) || getMaterial(MaterialType.COMPONENT, name, materials);
};
/** 获取插件素材 */
var getPlugin = function (schemaOrInstance, materials) {
    var id = schemaOrInstance.id, name = schemaOrInstance.name;
    return getMaterial(MaterialType.PLUGIN, id, materials) || getMaterial(MaterialType.PLUGIN, name, materials);
};
/**
 * parseStyles方法为对外暴露的接口，有可能在外部调用，平台解析完style以后传进来
 */
export var parseStyles = function (component, autoParseStyle) {
    var commonStyle = component.commonStyle, wrapperStyle = component.wrapperStyle;
    if (!autoParseStyle) {
        return { commonStyle: commonStyle, wrapperStyle: wrapperStyle };
    }
    var styles = {};
    if (commonStyle) {
        var position = utils.getPosition(commonStyle);
        if (position) {
            styles = { position: position };
        }
        styles = __assign(__assign({}, styles), { commonStyle: utils.mergeCommonStyle(commonStyle) });
    }
    if (wrapperStyle) {
        styles = __assign(__assign({}, styles), { wrapperStyle: utils.mergeCommonStyle(wrapperStyle) });
    }
    return styles;
};
/** 创建动作实例 */
export var createActionInstance = function (schema, materials, options) {
    if (options === void 0) { options = {}; }
    var renderStore = options.renderStore;
    var action = getAction(schema, materials);
    if (!action) {
        console.error("Cannot find action id[" + schema.id + "]");
        return null;
    }
    var actionInstance = __assign(__assign({}, R.clone(schema)), { action: action });
    renderStore === null || renderStore === void 0 ? void 0 : renderStore.registerSchemaStore(action.store, actionInstance);
    setMaxId(actionInstance.key);
    return actionInstance;
};
/** 创建热区实例 */
export var createHotAreaInstance = function (schema, materials, options) {
    if (options === void 0) { options = {}; }
    var actions = schema.actions;
    var hotAreaInstance = __assign(__assign({}, R.clone(schema)), { actions: actions
            .map(function (action) { return createActionInstance(action, materials, options); })
            .filter(function (i) { return !!i; }) });
    setMaxId(hotAreaInstance.key);
    return hotAreaInstance;
};
/** 创建组件实例 */
export var createComponentInstance = function (schema, materials, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var actions = schema.actions, children = schema.children, rest = __rest(schema, ["actions", "children"]);
    var _b = options.autoParseStyle, autoParseStyle = _b === void 0 ? false : _b, renderStore = options.renderStore, parent = options.parent;
    var styles = parseStyles(schema, autoParseStyle);
    var component = getComponent(schema, materials);
    if (!component) {
        console.error("Cannot find component id[" + schema.id + "]");
        return null;
    }
    var actionsInstances = actions
        .map(function (action) { return createActionInstance(action, materials, options); })
        .filter(function (i) { return !!i; });
    var componentInstance = __assign(__assign(__assign({}, R.clone(rest)), styles), { parent: parent,
        component: component, children: undefined, actions: actionsInstances });
    if (Array.isArray(children)) {
        componentInstance.children =
            (_a = children
                .map(function (child) {
                return createComponentInstance(child, materials, __assign(__assign({}, options), { parent: componentInstance }));
            })
                .filter(function (i) { return !!i; })) !== null && _a !== void 0 ? _a : [];
    }
    // 支持热区
    if (component.__implementHotArea) {
        var hotAreas = componentInstance.data.hotAreas;
        componentInstance.data.hotAreas = hotAreas === null || hotAreas === void 0 ? void 0 : hotAreas.map(function (hotArea) { return createHotAreaInstance(hotArea, materials, options); }).filter(function (i) { return !!i; });
        componentInstance.hotAreas = componentInstance.data.hotAreas;
    }
    renderStore === null || renderStore === void 0 ? void 0 : renderStore.registerSchemaStore(component.store, componentInstance);
    setMaxId(componentInstance.key);
    return componentInstance;
};
/** 创建插件实例 */
export var createPluginInstance = function (schema, materials /* , options: CreateOptions = {} */) {
    // const { renderStore } = options;
    var plugin = getPlugin(schema, materials);
    if (!plugin) {
        console.error("Cannot find plugin id[" + schema.id + "]");
        return null;
    }
    var pluginInstance = __assign(__assign({}, R.clone(schema)), { plugin: plugin });
    // renderStore?.registerSchemaStore(plugin.store, pluginInstance);
    setMaxId(pluginInstance.key);
    return pluginInstance;
};
/** 解析协议 */
export var parseSchema = function (schema, materials, options) {
    if (options === void 0) { options = {}; }
    var components = schema.components, plugins = schema.plugins, others = __rest(schema, ["components", "plugins"]);
    resetMaxId();
    return __assign({ componentInstances: components
            .map(function (component) { return createComponentInstance(component, materials, options); })
            .filter(function (i) { return !!i; }), pluginInstances: plugins
            .map(function (plugin) { return createPluginInstance(plugin, materials); })
            .filter(function (i) { return !!i; }) }, others);
};
/** 标准化动作实例 */
export var formatActionInstance = function (instance, materials, options) {
    if (options === void 0) { options = {}; }
    var renderStore = options.renderStore;
    var action = getAction(instance, materials);
    if (action) {
        // fix #270
        // 因为自定义组件事件使用相同的actionid，该action没有具体的dataForm信息，
        // 而自定义组件事件的dataForm是在编辑器中注入的，所以此处改为合并避免信息丢失
        instance.action = __assign(__assign({}, instance.action), action);
        renderStore === null || renderStore === void 0 ? void 0 : renderStore.registerSchemaStore(action.store, instance);
    }
    else {
        console.error("Cannot find action id[" + instance.id + "]");
    }
    return instance;
};
/** 标准化组件实例 */
export var formatComponentInstance = function (instance, materials, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var renderStore = options.renderStore;
    var component = getComponent(instance, materials);
    if (component) {
        instance.component = component;
        renderStore === null || renderStore === void 0 ? void 0 : renderStore.registerSchemaStore(component.store, instance);
    }
    else {
        console.error("Cannot find component id[" + instance.id + "]");
    }
    if (instance.actions.length) {
        instance.actions.forEach(function (action) { return formatActionInstance(action, materials, options); });
    }
    if ((_a = instance.children) === null || _a === void 0 ? void 0 : _a.length) {
        instance.children.forEach(function (child) { return formatComponentInstance(child, materials, options); });
    }
    return instance;
};
/** 标准化插件实例 */
export var formatPluginInstance = function (instance, materials) {
    // const { renderStore } = options;
    var plugin = getPlugin(instance, materials);
    if (plugin) {
        instance.plugin = plugin;
        // renderStore?.registerSchemaStore(plugin.store, instance);
    }
    else {
        console.error("Cannot find plugin id[" + instance.id + "]");
    }
    return instance;
};
/** 标准化实例 */
export var formatInstance = function (instance, materials, options) {
    if (options === void 0) { options = {}; }
    instance.componentInstances.forEach(function (componentInstance) {
        return formatComponentInstance(componentInstance, materials, options);
    });
    instance.pluginInstances.forEach(function (pluginInstance) { return formatPluginInstance(pluginInstance, materials); });
};
//# sourceMappingURL=SchemaParser.js.map